import { useCallback, useEffect, useState } from 'react';
import { ApiError } from '../../../../../shared/shared.api';
import { getTruckSchedule, type TruckScheduleEntry } from '../workshop-bookings.api';

export type TruckScheduleState = { status: 'loading' } | { status: 'loaded'; entries: TruckScheduleEntry[] } | { status: 'forbidden' } | { status: 'error' };

/** How far ahead the calendar looks: a service is booked weeks out, and a longer window would list trips nobody is planning around yet. */
const SCHEDULE_DAYS_AHEAD = 90;

function scheduleRange(): { from: Date; to: Date } {
  const from = new Date();
  from.setHours(0, 0, 0, 0);
  return { from, to: new Date(from.getTime() + SCHEDULE_DAYS_AHEAD * 24 * 60 * 60 * 1000) };
}

/** A truck's bookings and committed trip for the next 90 days, in order; `reload` re-reads after a booking changes. */
export function useTruckSchedule(truckId: string) {
  const [range] = useState(scheduleRange);
  const [state, setState] = useState<TruckScheduleState>({ status: 'loading' });

  const load = useCallback(async (): Promise<TruckScheduleState> => {
    try {
      return { status: 'loaded', entries: await getTruckSchedule(truckId, range) };
    } catch (err) {
      return { status: err instanceof ApiError && err.status === 403 ? 'forbidden' : 'error' };
    }
  }, [truckId, range]);

  useEffect(() => {
    let cancelled = false;
    load().then((next) => !cancelled && setState(next));
    return () => {
      cancelled = true;
    };
  }, [load]);

  return { state, reload: async () => setState(await load()) };
}
